import { useCallback, useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router'
import { ComponentWithPermissions } from '../../../../../../../baseComponents/CreateRoutering/types'
import EditCardTabs from '../../../../../../../baseComponents/EditCardTabs'
import { twinFetchPostJSON } from '../../../../../../../utils/globals/data'
import { getBasePath } from '../../../../../../../utils/globals/link'
import { ComponentTabObject } from '../../../../../../../utils/hooks/useTabLogic'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import MainContactB2BProfile from './Tabs/MainContactB2BProfile'
import ContactB2BOtherInformation from './Tabs/ContactB2BOtherInformation'
import { useBasicData } from '../../../../../../../utils/hooks/useBasicData'
import { dictionaryComplexFromJsonArr, TwinDictionary } from '../../../../../../../utils/globals/dictionary'
import CustomerAddresses from '../../../../../../../specificComponents/Customers/CustomerProfileTabs/CustomerAddresses'
import CustomerBanks from '../../../../../../../specificComponents/Customers/CustomerProfileTabs/CustomerBanks'
import CustomerContactsPersons from '../../../../../../../specificComponents/Customers/CustomerProfileTabs/CustomerContactsPersons'
import CustomerProjects from '../../../../../../../specificComponents/Customers/CustomerProfileTabs/CustomerProjects'
import CustomerBagOfHours from '../../../../../../../specificComponents/Customers/CustomerProfileTabs/CustomerBagOfHours'
import CustomerTasks from '../../../../../../../specificComponents/Customers/CustomerProfileTabs/CustomerTasks'
import CustomerCampaigns from '../../../../../../../specificComponents/Customers/CustomerProfileTabs/CustomerCampaigns'
import CustomerNotes from '../../../../../../../specificComponents/Customers/CustomerProfileTabs/CustomerNotes'
import CustomerFiles from '../../../../../../../specificComponents/Customers/CustomerProfileTabs/CustomerFiles'
import CustomerWorkingCenters from '../../../../../../../specificComponents/Customers/CustomerProfileTabs/CustomerWorkingCenters'
import CustomerSellOrders from '../../../../../../../specificComponents/Customers/CustomerProfileTabs/CustomerSellOrders'
import CustomerBillingGroups from '../../../../../../../specificComponents/Customers/CustomerProfileTabs/CustomerBillingGroups'
import CustomerDashboard from '../../../../../../../specificComponents/Customers/CustomerProfileTabs/CustomerDashboard'

interface SingleContactB2BProps extends  ComponentWithPermissions {}

const SingleContactB2B: React.FC<SingleContactB2BProps> = ({ userPermissions }) => {
    const location = useLocation()
    const params = useParams<{ id: string, tab?: string }>()
    const { t } = useTwinTranslation()
    const { basicData, getData } = useBasicData({ params: { id: params.id }, url: '/api/app/customer/getCustomerInstance', variableToCurrentPage: 'name' })
    const { dictCustomerOrigins } = useSingleContactB2BLogic()

    if (!params.id) {
        return null
    }
    const tabs: ComponentTabObject = {
        'main': { component: CustomerDashboard, text: t('dashboard', 'Dashboard'), permission: 'customer.profile.main', skipWhiteBox: true },
        'general': { component: MainContactB2BProfile, text: t('general', 'General'), permission: 'customer.profile.main', extraProps: { dictCustomerOrigins } },
        'activity': { component: CustomerNotes, text: t('activity', 'Actividad'), permission: 'customer.profile.notes' },
        'people': { component: CustomerContactsPersons, text: t('people', 'Personas'), permission: 'customer.profile.contactPerson' },
        'workingCenters': { component: CustomerWorkingCenters, text: t('workingCenters', 'Centros de trabajo'), permission: 'customer.profile.shops' },
        'address': { component: CustomerAddresses, text: t('addresses', 'Direcciones'), permission: 'customer.profile.address' },
        'tasks': { component: CustomerTasks, text: t('tasks', 'Tareas'), permission: 'customer.profile.tasks', modules: ['tasks'] },
        'sells': { component: CustomerSellOrders, text: t('sells', 'Ventas'), permission: 'customer.profile.quotes', modules: ['quotes'] },
        'projects': { component: CustomerProjects, text: t('projects', 'Proyectos'), permission: 'customer.profile.projects' },
        'campaigns': { component: CustomerCampaigns, text: t('campaigns', 'Campañas'), permission: 'customer.profile.campaign', modules: ['tasks'] },
        'bagsOfHours': { component: CustomerBagOfHours, text: t('bagsOfHours', 'Bolsas de horas'), permission: 'customer.profile.bagOfHours', modules: ['tasks'] },
        'bank': { component: CustomerBanks, text: t('banks', 'Bancos'), permission: 'customer.profile.bank' },
        'billingGroups': { component: CustomerBillingGroups, text: t('billingGroups', 'Grupos de Facturación'), permission: 'customer.profile.billingGroup' },
        'files': { component: CustomerFiles, text: t('files', 'Ficheros'), permission: 'customer.profile.files' },
        'other-information': { component: ContactB2BOtherInformation, text: t('otherInformation', 'Otra información'), permission: 'customer.profile.main' },
    }

    return (<EditCardTabs basePath={getBasePath(location.pathname, params.id)} basicData={basicData} tabs={tabs} tab={params.tab} userPermissions={userPermissions} getData={getData} />)
}



const useSingleContactB2BLogic = () => {
    const [dictCustomerOrigins, setDictCustomerOrigins] = useState<TwinDictionary | null>(null)

    const getDictCustomerOrigins = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/customer/customerOrigin/getAllCustomerOrigins', {})
        if (result) {
            setDictCustomerOrigins(dictionaryComplexFromJsonArr(result))
        }
    }, [setDictCustomerOrigins])

    useEffect(() => {
        getDictCustomerOrigins()
    }, [getDictCustomerOrigins])

    return { dictCustomerOrigins }
}

export default SingleContactB2B